import { useMemo, type ReactElement, type ReactNode } from 'react'
import { Badge } from 'app/components/ui/badge'
import type { CallAi } from 'app/generated/graphql'
import { elevenLabsCollection, trackableCollection, type DataGroup, type DataPoint } from 'app/lib/dataCollectionGroups'

type Props = {
  /** Trackable's call processing extraction (`Call.ai`). */
  ai?: CallAi | null
  /** The ElevenLabs CallData record (`extractedData` + raw `data` payload). */
  callData?: unknown
}

/**
 * The Data Collection tab of a call activity: everything AI extracted from the
 * call, grouped by source (ElevenLabs agent, Trackable call processing) and topic.
 */
export function CallDataCollection({ ai, callData }: Props): ReactElement {
  const elevenLabs = useMemo(() => elevenLabsCollection(callData), [callData])
  const trackable = useMemo(() => trackableCollection(ai), [ai])

  if (!elevenLabs && !trackable) {
    return <p className="py-6 text-center text-body-xs text-subtle">No AI-extracted data for this call yet.</p>
  }

  return (
    <div className="flex flex-col gap-y-6 py-3">
      {elevenLabs && (
        <SourceSection title="ElevenLabs" description="Collected by the voice agent after the call">
          {(elevenLabs.title || elevenLabs.summary || elevenLabs.callSuccessful) && (
            <div className="flex flex-col gap-y-1.5 rounded-lg border border-default bg-surface-subtle p-3">
              <div className="flex items-start justify-between gap-3">
                <span className="text-label-xs text-strong">{elevenLabs.title || `Call summary`}</span>
                {elevenLabs.callSuccessful && (
                  <Badge
                    label={elevenLabs.callSuccessful.label}
                    color={elevenLabs.callSuccessful.color}
                    className="shrink-0"
                  />
                )}
              </div>
              {elevenLabs.summary && <p className="text-body-xs text-default">{elevenLabs.summary}</p>}
            </div>
          )}
          {elevenLabs.groups.map((group) => (
            <GroupCard key={group.title} group={group} />
          ))}
        </SourceSection>
      )}
      {trackable && (
        <SourceSection title="Trackable" description="Extracted by Trackable call processing">
          <GroupCard group={trackable.overview} />
          {trackable.people.map((person) => (
            <div key={person.key} className="flex flex-col gap-y-2">
              <div className="flex items-center gap-x-2">
                <span className="text-label-xs text-strong">{person.name}</span>
                {person.isCaller && trackable.people.length > 1 && <Badge label="Caller" color="blue" />}
              </div>
              {person.groups.map((group) => (
                <GroupCard key={group.title} group={group} />
              ))}
            </div>
          ))}
          <GroupCard group={trackable.extraction} />
        </SourceSection>
      )}
    </div>
  )
}

function SourceSection({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: ReactNode
}): ReactElement {
  return (
    <section className="flex flex-col gap-y-3">
      <div className="flex flex-col gap-y-0.5 border-b border-default pb-2">
        <h3 className="text-heading-xs text-strong">{title}</h3>
        <p className="text-caption-md text-subtle">{description}</p>
      </div>
      {children}
    </section>
  )
}

function GroupCard({ group }: { group: DataGroup }): ReactElement {
  return (
    <div className="overflow-hidden rounded-lg border border-default">
      <div className="border-b border-default bg-surface-subtle px-3 py-1.5 text-label-xs text-subtle">
        {group.title}
      </div>
      <div className="flex flex-col divide-y divide-default bg-surface">
        {group.points.map((point) => (
          <DataRow key={point.id} point={point} />
        ))}
      </div>
    </div>
  )
}

function DataRow({ point }: { point: DataPoint }): ReactElement {
  // Prose (summaries, action items) reads better under its label than squeezed to the right.
  const stacked = (point.value?.length ?? 0) > 60
  return (
    <div className="flex flex-col gap-y-1 px-3 py-2">
      <div className="flex items-start justify-between gap-x-3">
        <span className="shrink-0 text-body-xs text-subtle">{point.label}</span>
        {!point.fields && !stacked && <DataValue point={point} />}
      </div>
      {stacked && <p className="whitespace-pre-line break-words text-body-xs text-strong">{point.value}</p>}
      {point.fields && (
        <div className="grid grid-cols-1 gap-x-4 gap-y-1 rounded bg-surface-subtle px-2 py-1.5 sm:grid-cols-2">
          {point.fields.map((field) => (
            <div key={field.id} className="flex items-start justify-between gap-x-2">
              <span className="text-caption-md text-subtle">{field.label}</span>
              <DataValue point={field} />
            </div>
          ))}
        </div>
      )}
      {point.rationale && <p className="text-caption-md text-subtle">{point.rationale}</p>}
    </div>
  )
}

function DataValue({ point }: { point: DataPoint }): ReactElement {
  if (point.badges) {
    return (
      <div className="flex flex-wrap justify-end gap-1">
        {point.badges.map((badge) => (
          <Badge key={badge.label} label={badge.label} color={badge.color} />
        ))}
      </div>
    )
  }
  if (point.value) {
    return (
      <span className="min-w-0 whitespace-pre-line break-words text-right text-ui-xs text-strong">{point.value}</span>
    )
  }
  return <span className="text-ui-xs text-minimal">—</span>
}
